import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import {
  useUpdateTask,
  useListProjects,
  getGetTodayQueryKey,
  getGetProjectQueryKey,
  getListProjectsQueryKey,
  getListTasksQueryKey,
  getGetInboxQueryKey,
  type Task,
} from "@workspace/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";

const NO_PROJECT = "__none__";

const PRIORITY_OPTIONS = [
  { value: "low", label: "Basse" },
  { value: "medium", label: "Moyenne" },
  { value: "high", label: "Haute" },
];

const STATUS_OPTIONS = [
  { value: "todo", label: "À faire" },
  { value: "doing", label: "En cours" },
  { value: "done", label: "Terminée" },
];

function toDateInput(iso: string | null | undefined): string {
  if (!iso) return "";
  const m = /^(\d{4}-\d{2}-\d{2})/.exec(iso);
  if (m) return m[1];
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const y = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, "0");
  const da = String(d.getDate()).padStart(2, "0");
  return `${y}-${mo}-${da}`;
}

export interface TaskEditDialogProps {
  task: Task;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * Optional: an inbox item id whose query should also be invalidated after
   * the update succeeds (e.g. when the dialog is opened from inbox flows).
   */
  inboxItemId?: string;
}

export default function TaskEditDialog({
  task,
  open,
  onOpenChange,
  inboxItemId,
}: TaskEditDialogProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const updateTask = useUpdateTask();

  const projectsQuery = useListProjects(undefined, {
    query: {
      queryKey: getListProjectsQueryKey(),
      enabled: open,
      staleTime: 60_000,
    },
  });

  const [title, setTitle] = useState(task.title);
  const [description, setDescription] = useState(task.description ?? "");
  const [projectId, setProjectId] = useState(task.projectId ?? "");
  const [dueDate, setDueDate] = useState(toDateInput(task.dueDate));
  const [priority, setPriority] = useState<string>(task.priority ?? "medium");
  const [status, setStatus] = useState<string>(task.status ?? "todo");

  // Re-sync local state whenever the dialog is (re)opened with a new task.
  useEffect(() => {
    if (!open) return;
    setTitle(task.title);
    setDescription(task.description ?? "");
    setProjectId(task.projectId ?? "");
    setDueDate(toDateInput(task.dueDate));
    setPriority(task.priority ?? "medium");
    setStatus(task.status ?? "todo");
  }, [open, task]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) {
      toast({ title: "Le titre est obligatoire", variant: "destructive" });
      return;
    }
    try {
      await updateTask.mutateAsync({
        id: task.id,
        data: {
          title: title.trim(),
          description: description.trim() ? description.trim() : null,
          projectId: projectId || null,
          // Use local NOON (not midnight) so the calendar day never flips to
          // the previous day when converted to UTC for positive-offset users —
          // otherwise a "due today" task slips into "overdue" and seems to vanish.
          dueDate: dueDate ? new Date(`${dueDate}T12:00:00`).toISOString() : null,
          priority,
          status,
        },
      });

      // Invalidate every list/detail that may now be stale.
      queryClient.invalidateQueries({ queryKey: getGetTodayQueryKey() });
      queryClient.invalidateQueries({ queryKey: getListProjectsQueryKey() });
      queryClient.invalidateQueries({
        queryKey: getListTasksQueryKey(),
        exact: false,
      });
      // The task may have moved between projects — invalidate both old & new.
      if (task.projectId) {
        queryClient.invalidateQueries({
          queryKey: getGetProjectQueryKey(task.projectId),
        });
      }
      if (projectId && projectId !== task.projectId) {
        queryClient.invalidateQueries({
          queryKey: getGetProjectQueryKey(projectId),
        });
      }
      if (inboxItemId) {
        queryClient.invalidateQueries({
          queryKey: getGetInboxQueryKey(inboxItemId),
        });
      }

      toast({ title: "Tâche mise à jour" });
      onOpenChange(false);
    } catch {
      toast({ title: "Échec de la mise à jour", variant: "destructive" });
    }
  };

  const projects = projectsQuery.data ?? [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-lg"
        data-testid="dialog-edit-task"
      >
        <DialogHeader>
          <DialogTitle>Modifier la tâche</DialogTitle>
          <DialogDescription>
            Ajustez le titre, le projet, l'échéance, la priorité et le statut.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="task-edit-title">Titre</Label>
            <Input
              id="task-edit-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              autoFocus
              required
              data-testid="input-edit-task-title"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="task-edit-description">Description</Label>
            <Textarea
              id="task-edit-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              placeholder="Notes, contexte, lien…"
              className="resize-none"
              data-testid="input-edit-task-description"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="task-edit-project">Projet</Label>
            <Select
              value={projectId || NO_PROJECT}
              onValueChange={(v) => setProjectId(v === NO_PROJECT ? "" : v)}
            >
              <SelectTrigger
                id="task-edit-project"
                data-testid="select-edit-task-project"
              >
                <SelectValue placeholder="Aucun projet" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NO_PROJECT}>Aucun projet</SelectItem>
                {projects.map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="task-edit-due">Échéance</Label>
              <Input
                id="task-edit-due"
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                data-testid="input-edit-task-due"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="task-edit-priority">Priorité</Label>
              <Select value={priority} onValueChange={setPriority}>
                <SelectTrigger
                  id="task-edit-priority"
                  data-testid="select-edit-task-priority"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PRIORITY_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="task-edit-status">Statut</Label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger
                  id="task-edit-status"
                  data-testid="select-edit-task-status"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STATUS_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter className="gap-2 sm:gap-2">
            <Button
              type="button"
              variant="ghost"
              onClick={() => onOpenChange(false)}
              disabled={updateTask.isPending}
            >
              Annuler
            </Button>
            <Button
              type="submit"
              disabled={updateTask.isPending || !title.trim()}
              data-testid="button-save-task-edit"
            >
              {updateTask.isPending && (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              )}
              Enregistrer
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
